import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'bitwheel.freePlay';
const BASE_FREE_GAMES = 3;

const defaultState = {
  totalGranted: BASE_FREE_GAMES,
  used: 0,
  emailSignupGranted: false,
  purchases: [],
  lastUpdated: null,
  signupEmail: null
};

const FreePlayContext = createContext();

const readStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    return parsed;
  } catch (error) {
    console.error('Failed to read free play state:', error);
    return null;
  }
};

const writeStorage = (value) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch (error) {
    console.error('Failed to write free play state:', error);
  }
};

export const FreePlayProvider = ({ children }) => {
  const [state, setState] = useState(() => {
    const stored = readStorage();
    if (!stored) {
      return { ...defaultState };
    }
    const totalGranted = Number.isFinite(stored.totalGranted)
      ? Math.max(stored.totalGranted, BASE_FREE_GAMES)
      : BASE_FREE_GAMES;
    const used = Number.isFinite(stored.used) ? Math.max(0, stored.used) : 0;

    return {
      ...defaultState,
      ...stored,
      totalGranted,
      used,
      emailSignupGranted: Boolean(stored.emailSignupGranted),
      purchases: Array.isArray(stored.purchases) ? stored.purchases : [],
      lastUpdated: stored.lastUpdated ?? null
    };
  });
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [paymentReason, setPaymentReason] = useState(null);

  useEffect(() => {
    writeStorage(state);
  }, [state]);

  const freeGamesRemaining = useMemo(
    () => Math.max(0, Math.floor(state.totalGranted - state.used)),
    [state.totalGranted, state.used]
  );

  const canPlayFreeGame = useCallback(() => freeGamesRemaining > 0, [freeGamesRemaining]);

  const recordFreeGamePlayed = useCallback(() => {
    setState((prev) => {
      const remaining = Math.max(0, Math.floor(prev.totalGranted - prev.used));
      if (remaining <= 0) {
        return prev;
      }

      return {
        ...prev,
        used: prev.used + 1,
        lastUpdated: Date.now()
      };
    });
  }, []);

  const grantFreeGames = useCallback((count, { source, email } = {}) => {
    if (!Number.isFinite(count) || count <= 0) {
      return;
    }

    setState((prev) => ({
      ...prev,
      totalGranted: prev.totalGranted + count,
      emailSignupGranted: source === 'signup' ? true : prev.emailSignupGranted,
      purchases:
        source === 'purchase'
          ? [
            ...prev.purchases,
            {
              count,
              email: email ?? null,
              grantedAt: Date.now()
            }
          ]
          : prev.purchases,
      lastUpdated: Date.now()
    }));
  }, []);

  const markEmailSignup = useCallback((email) => {
    setState((prev) => {
      if (prev.emailSignupGranted) {
        return prev;
      }

      return {
        ...prev,
        emailSignupGranted: true,
        totalGranted: prev.totalGranted + 3,
        lastUpdated: Date.now(),
        signupEmail: email ?? prev.signupEmail ?? null
      };
    });
  }, []);

  const resetFreeGames = useCallback(() => {
    setState({ ...defaultState });
  }, []);

  const openPayment = useCallback((reason = null) => {
    setPaymentReason(reason);
    setIsPaymentOpen(true);
  }, []);

  const closePayment = useCallback(() => {
    setIsPaymentOpen(false);
    setPaymentReason(null);
  }, []);

  const value = useMemo(
    () => ({
      state,
      freeGamesRemaining,
      canPlayFreeGame,
      recordFreeGamePlayed,
      grantFreeGames,
      markEmailSignup,
      resetFreeGames,
      isPaymentOpen,
      openPayment,
      closePayment,
      paymentReason
    }),
    [
      state,
      freeGamesRemaining,
      canPlayFreeGame,
      recordFreeGamePlayed,
      grantFreeGames,
      markEmailSignup,
      resetFreeGames,
      isPaymentOpen,
      openPayment,
      closePayment,
      paymentReason
    ]
  );

  return (
    <FreePlayContext.Provider value={value}>
      {children}
    </FreePlayContext.Provider>
  );
};

export const useFreePlay = () => {
  const context = useContext(FreePlayContext);
  if (!context) {
    throw new Error('useFreePlay must be used within a FreePlayProvider');
  }
  return context;
};

export default FreePlayContext;
